import React, {useEffect, useState} from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Link } from "gatsby"
import { MainBackground } from "../MainBackground"
import styled from "styled-components"
import { Container } from "../Container"
import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import {Icon} from "../Icon";

const Event = ({ data, location }) => {
  const {
    title,
    start_date: startDate,
    custom_date: customDate,
    description,
    description_richtext: { html: htmlDescription },
    featured_image: image,
    register_url: registerURL,
    organiser,
    address,
    category,
  } = data;
  const [top, setTop] = useState(false);
  const handleScroll = () => {
    if ((window.scrollY > 1500) && (!top)) {
      setTop(true);
    } else {
      setTop(false);
    }
  };
  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const fromUrl = new URLSearchParams(location?.search).get("from")
  const categoryColor = category?.document?.data?.color;
  const breadCrumbs = [
    { label: "Return events", linkUrl: fromUrl || "/event" },
    {
      label: `${category?.document?.data?.name}`,
      linkUrl: `/category/${category?.uid}`,
    },
  ]

  const eventimage = getImage(image)

  return (
    <MainBackground type="violet" container>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap')
      </style>
      <Box as="section">
        <Container variant="content">
          <BreadCrumbs list={breadCrumbs} />
          <Subtitle>{title.text}</Subtitle>
          <div>
            <EventInfo>
              <DateEvent>
                <p>Date and Time:</p>
                <p>{customDate}</p>
              </DateEvent>
              <LocationEvent>
                <p>Location:</p>
                <p>{address}</p>
              </LocationEvent>
              <TimeWrapper>
                <p>{moment.utc(startDate).format("D MMM h:mm A")}</p>
              </TimeWrapper>
            </EventInfo>
            <RegisterBtn>
              <Link to={registerURL.url} target="_blank">
                Click to register
              </Link>
            </RegisterBtn>
            <Organisers>
              <h4>Organisers:</h4>
              <p>{organiser}</p>
            </Organisers>
          </div>
        </Container>
        <EventContainer>
          {top && <TopBtn onClick={scrollTop}>
            <Icon type="SCROLL_TOP_BLOCK_WHITE"/>
          </TopBtn>}
          <Container variant="content">
            <SubTitle>
              <ContentDescription color={categoryColor} variant="webinar.label">
                <h3>Description</h3>
              </ContentDescription>
            </SubTitle>
            <HeadWrap>
              {image && (
                <ImageWrap>
                  <GatsbyImage image={eventimage} alt={title.text ?? ""} />
                </ImageWrap>
              )}
            </HeadWrap>
            {htmlDescription.length > 0 ? (
              <EventContent
                variant="webinar.text"
                dangerouslySetInnerHTML={{ __html: htmlDescription }}
              />
            ) : (
              <div variant="webinar.text">{description}</div>
            )}
          </Container>
        </EventContainer>
      </Box>
    </MainBackground>
  )
}

Event.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Event

const Box = styled.div`
  background-color: transparent;
  position: relative;
`
const EventContent = styled.div`
  p {
    font-family: Roboto, sans-serif !important;
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 32px;
  }

  a {
    color: #5586de;
    text-decoration: underline;
  }

  @media (max-width: 599px) {
    p {
      font-size: 15px;
      line-height: 25px;
      margin-bottom: 15px;
    }
  }
`
const EventContainer = styled.div`
  background: rgb(249, 249, 249);
  padding-bottom: 5px;
`
const ContentDescription = styled.div`
  padding-top: 20px;
  color: ${props => props.color};
`
const EventInfo = styled.div`
  color: white;
  display: flex;
  flex-wrap: wrap;
`

export const Subtitle = styled.h2`
  font-size: 25px;
  line-height: 28px;
  text-transform: uppercase;
  color: white;
  font-weight: 700;
  margin-bottom: 10px;

  @media (min-width: ${({ theme }) => theme.media.tablet_min}) {
    color: #fff;
    font-size: 39px;
    text-transform: uppercase;
    line-height: calc(78 / 64);
    margin-bottom: 5px;
  }
`

const Organisers = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
  h4,
  p {
    margin-right: 5px;
    font-size: 20px;
    line-height: 20px;
    color: white;
    margin-bottom: 0;
  }
  h4 {
    font-weight: 700;
    margin-bottom: 5px;
  }

  p {
    font-size: 18px;
    margin-bottom: 5px;
  }
`
const TopBtn = styled.button`
  cursor: pointer;
  width: 55px;
  height: 55px;
  background: rgb(60,49,94);
  position: fixed;
  border-radius: 50%;
  bottom: 120px;
  right: 70px;
  z-index: 9999;
  :before {
    position: absolute;
    content: '';
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    border-radius: 50%;
  }
  @media (max-width: 1024px) {
    bottom: 100px;
    width: 45px;
    height: 45px;
    overflow: hidden;
    right: 30px;
    svg {
      position: absolute;
      top: -6px;
      left: -4px;
    }
  }
  @media (max-width: 768px) {
    bottom: 30px;
  }
`

const DateEvent = styled.div`
  font-size: 20px;
  line-height: calc(38.4 / 24);
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  margin-right: 10px;
  p {
    margin-bottom: 0;
    &:first-child {
      margin-right: 5px;
      font-weight: 700;
      @media (min-width: ${({ theme }) => theme.media.mobile_max}) {
        margin-top: 0;
      }
    }
  }
`

const LocationEvent = styled.div`
  margin-right: 10px;
  display: flex;
  flex-wrap: wrap;
  p {
    margin: 0;
    &:first-child {
      font-weight: 700;
      margin-right: 5px;
    }
  }
`

const TimeWrapper = styled.div`
  p {
    margin-bottom: 0;
    font-weight: 600;
  }
`

const SubTitle = styled.div`
  h3 {
    margin-bottom: 20px;
    line-height: 33px;
    font-size: 32px;
    font-family: Roboto, sans-serif !important;
  }
`

const HeadWrap = styled.div`
  @media (min-width: ${({ theme }) => theme.media.mobile_max}) {
    display: flex;
    align-items: stretch;
    padding-bottom: 10px;
  }
`

const ImageWrap = styled.div`
  max-width: 600px;
  display: block;
  margin: 0 auto 20px;
`
const RegisterBtn = styled.div`
  a {
    ${({ theme }) => theme.primaryButton};
    display: inline-block;
    margin-top: 10px;
    @media (min-width: ${({ theme }) => theme.media.tablet_min}) {
      font-size: 20px;
      padding: 13px 20px;
    }
  }
`